import {useState, useEffect, useCallback, CSSProperties} from 'react'
import {Link} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../_metronic/helpers'

// Public testimonial-wall landing page (no auth) — /testimonials.
//
// Clips + poster frames live in DO Spaces under zipper/testimonials/ (uploaded by
// api.fbzipper.com/scripts/_upload-testimonial-videos.js). Nothing but the poster
// JPEGs load up front; the mp4 is only fetched once a card is opened.
const CDN = 'https://zipper.ams3.digitaloceanspaces.com/zipper/testimonials'

type Clip = {id: string; seconds: number}

// Ordered so the first row of the wall leads with the clearest, best-framed faces.
const CLIPS: Clip[] = [
  {id: 'v03', seconds: 87},
  {id: 'v27', seconds: 28},
  {id: 'v31', seconds: 16},
  {id: 'v21', seconds: 33},
  {id: 'v24', seconds: 48},
  {id: 'v29', seconds: 35},
  {id: 'v02', seconds: 21},
  {id: 'v13', seconds: 33},
  {id: 'v25', seconds: 68},
  {id: 'v32', seconds: 66},
  {id: 'v04', seconds: 37},
  {id: 'v07', seconds: 35},
  {id: 'v01', seconds: 21},
  {id: 'v05', seconds: 118},
  {id: 'v06', seconds: 47},
  {id: 'v08', seconds: 62},
  {id: 'v09', seconds: 28},
  {id: 'v10', seconds: 38},
  {id: 'v11', seconds: 15},
  {id: 'v12', seconds: 294},
  {id: 'v14', seconds: 21},
  {id: 'v15', seconds: 21},
  {id: 'v16', seconds: 20},
  {id: 'v17', seconds: 100},
  {id: 'v18', seconds: 15},
  {id: 'v19', seconds: 37},
  {id: 'v20', seconds: 21},
  {id: 'v22', seconds: 14},
  {id: 'v23', seconds: 49},
  {id: 'v26', seconds: 20},
  {id: 'v28', seconds: 24},
  {id: 'v30', seconds: 20},
]

const INITIAL_VISIBLE = 12

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`

const Chevron = ({dir}: {dir: 'left' | 'right'}) => (
  <svg
    viewBox='0 0 24 24'
    width='26'
    height='26'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d={dir === 'left' ? 'M15 5 8 12l7 7' : 'M9 5l7 7-7 7'} />
  </svg>
)

const TestimonialsPage = () => {
  const [open, setOpen] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)

  // Renders outside the <App> wrapper, so the boot splash is never auto-hidden.
  useEffect(() => {
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    document.title = 'לקוחות מספרים | fbzipper'
  }, [])

  const close = useCallback(() => setOpen(null), [])
  const step = useCallback((delta: number) => {
    setOpen((cur) => (cur === null ? cur : (cur + delta + CLIPS.length) % CLIPS.length))
  }, [])

  // Lightbox keyboard controls + scroll lock. RTL: ArrowLeft advances forward.
  useEffect(() => {
    if (open === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowLeft') step(1)
      else if (e.key === 'ArrowRight') step(-1)
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, close, step])

  const visible = showAll ? CLIPS : CLIPS.slice(0, INITIAL_VISIBLE)

  return (
    <div style={page}>
      <style>{css}</style>

      <header style={nav}>
        <div style={navInner}>
          <img
            src={toAbsoluteUrl('/media/logos/d-code-logo-light.svg')}
            alt='fbzipper'
            style={{height: 26}}
          />
          <Link to='/auth/register' className='tw-btn tw-btn-sm'>
            פתיחת חשבון
          </Link>
        </div>
      </header>

      <section style={hero}>
        <div style={glow} aria-hidden='true' />
        <div style={heroInner}>
          <span style={eyebrow}>לקוחות מספרים</span>
          <h1 style={h1}>
            אלפי עסקים כבר מפרסמים
            <br />
            בקבוצות פייסבוק <span style={grad}>אוטומטית</span>
          </h1>
          <p style={sub}>
            מעל 100 עדויות מצולמות של לקוחות fbzipper. לחצו על כל אחת כדי לצפות.
          </p>

          <div style={facepile}>
            <div style={faces}>
              {CLIPS.slice(0, 6).map((c, i) => (
                <img
                  key={c.id}
                  src={`${CDN}/${c.id}.jpg`}
                  alt=''
                  style={{...face, marginInlineStart: i === 0 ? 0 : -14, zIndex: 6 - i}}
                />
              ))}
            </div>
            <span style={facepileText}>מעל 100 לקוחות מספרים על המערכת</span>
          </div>

          <div style={ctaRow}>
            <Link to='/auth/register' className='tw-btn tw-btn-lg'>
              פתיחת חשבון
            </Link>
            <a href='#wall' className='tw-btn tw-btn-lg tw-btn-ghost'>
              לצפייה בעדויות
            </a>
          </div>
        </div>
      </section>

      <section id='wall' style={wall}>
        <div className='tw-grid'>
          {visible.map((c) => {
            const idx = CLIPS.indexOf(c)
            return (
              <button
                key={c.id}
                type='button'
                className='tw-card'
                onClick={() => setOpen(idx)}
                aria-label={`נגן עדות ${idx + 1} מתוך ${CLIPS.length}`}
              >
                <img src={`${CDN}/${c.id}.jpg`} alt='' loading='lazy' className='tw-thumb' />
                <span className='tw-shade' aria-hidden='true' />
                <span className='tw-play' aria-hidden='true'>
                  <svg viewBox='0 0 24 24' width='22' height='22' fill='currentColor'>
                    <path d='M8 5.14v13.72L19 12z' />
                  </svg>
                </span>
                <span className='tw-dur'>{fmt(c.seconds)}</span>
              </button>
            )
          })}
        </div>

        {!showAll && (
          <div style={{textAlign: 'center', marginTop: 32}}>
            <button type='button' className='tw-btn tw-btn-ghost' onClick={() => setShowAll(true)}>
              הצגת כל העדויות
            </button>
          </div>
        )}
      </section>

      <section style={closer}>
        <h2 style={h2}>אתה רוצה להיות הממליץ הבא?</h2>
        <p style={{...sub, marginBottom: 28}}>
          פרסום אוטומטי בקבוצות פייסבוק, מענה בתגובות ופרסום במרקטפלייס — במקום אחד.
        </p>
        <Link to='/auth/register' className='tw-btn tw-btn-lg'>
          פתיחת חשבון
        </Link>
      </section>

      <footer style={footer}>
        <span>© {new Date().getFullYear()} fbzipper</span>
        <Link to='/terms' style={footLink}>
          תנאי שימוש
        </Link>
        <Link to='/privacy' style={footLink}>
          מדיניות פרטיות
        </Link>
      </footer>

      {open !== null && (
        <div
          style={overlay}
          onClick={close}
          role='dialog'
          aria-modal='true'
          aria-label='נגן עדויות'
        >
          <button type='button' className='tw-x' onClick={close} aria-label='סגירה'>
            ✕
          </button>
          {/* RTL: "next" sits on the left. Chevrons are SVG, not ‹ / › — those are
              bidi-mirrored characters and flip direction inside an RTL block. */}
          <button
            type='button'
            className='tw-nav tw-nav-next'
            onClick={(e) => {
              e.stopPropagation()
              step(1)
            }}
            aria-label='העדות הבאה'
          >
            <Chevron dir='left' />
          </button>
          <button
            type='button'
            className='tw-nav tw-nav-prev'
            onClick={(e) => {
              e.stopPropagation()
              step(-1)
            }}
            aria-label='העדות הקודמת'
          >
            <Chevron dir='right' />
          </button>

          <div style={stage} onClick={(e) => e.stopPropagation()}>
            <video
              key={CLIPS[open].id}
              src={`${CDN}/${CLIPS[open].id}.mp4`}
              poster={`${CDN}/${CLIPS[open].id}.jpg`}
              controls
              autoPlay
              playsInline
              preload='auto'
              className='tw-video'
              // The <video> only mounts after the card click, so the autoPlay attribute
              // can fire before the media is ready and get dropped. Retry once it can
              // play; if Chrome still refuses, the native play button is right there.
              onCanPlay={(e) => {
                const el = e.currentTarget
                if (el.paused) el.play().catch(() => {})
              }}
            />
            <div style={stageMeta}>
              <span>
                עדות {open + 1} מתוך {CLIPS.length}
              </span>
              <Link to='/auth/register' className='tw-btn tw-btn-sm'>
                פתיחת חשבון
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- styles */

const INK = '#0B1120'
const ACCENT = '#009ef7'

const page: CSSProperties = {
  direction: 'rtl',
  minHeight: '100vh',
  background: INK,
  color: '#F1F5F9',
  fontFamily: 'inherit',
  overflowX: 'hidden',
}

const nav: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 30,
  background: 'rgba(11,17,32,.82)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid rgba(255,255,255,.07)',
}

const navInner: CSSProperties = {
  maxWidth: 1180,
  margin: '0 auto',
  padding: '14px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
}

const hero: CSSProperties = {position: 'relative', padding: '76px 20px 56px', textAlign: 'center'}

const glow: CSSProperties = {
  position: 'absolute',
  top: -260,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 900,
  height: 620,
  background: 'radial-gradient(closest-side, rgba(0,158,247,.28), transparent 70%)',
  pointerEvents: 'none',
}

const heroInner: CSSProperties = {position: 'relative', maxWidth: 780, margin: '0 auto'}

const eyebrow: CSSProperties = {
  display: 'inline-block',
  padding: '6px 14px',
  borderRadius: 999,
  border: '1px solid rgba(0,158,247,.35)',
  background: 'rgba(0,158,247,.1)',
  color: '#7DD3FC',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '.02em',
}

const h1: CSSProperties = {
  margin: '22px 0 0',
  fontSize: 'clamp(30px, 5.4vw, 52px)',
  lineHeight: 1.18,
  fontWeight: 800,
  letterSpacing: '-.01em',
  color: '#fff',
}

const grad: CSSProperties = {
  background: `linear-gradient(95deg, ${ACCENT}, #67E8F9)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}

const h2: CSSProperties = {
  margin: '0 0 12px',
  fontSize: 'clamp(24px, 3.6vw, 36px)',
  fontWeight: 800,
  color: '#fff',
}

const sub: CSSProperties = {
  margin: '18px auto 0',
  maxWidth: 560,
  fontSize: 'clamp(15px, 2vw, 18px)',
  lineHeight: 1.7,
  color: '#94A3B8',
}

const facepile: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  margin: '28px 0 0',
  flexWrap: 'wrap',
}

const faces: CSSProperties = {display: 'flex', alignItems: 'center'}

const face: CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #0B1120',
  boxShadow: '0 0 0 1px rgba(255,255,255,.12)',
  position: 'relative',
}

const facepileText: CSSProperties = {fontSize: 14, color: '#94A3B8'}

const ctaRow: CSSProperties = {
  display: 'flex',
  gap: 12,
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginTop: 34,
}

const wall: CSSProperties = {maxWidth: 1180, margin: '0 auto', padding: '28px 20px 80px'}

const closer: CSSProperties = {
  borderTop: '1px solid rgba(255,255,255,.07)',
  padding: '72px 20px 80px',
  textAlign: 'center',
  background: 'linear-gradient(180deg, rgba(0,158,247,.07), transparent 60%)',
}

const footer: CSSProperties = {
  borderTop: '1px solid rgba(255,255,255,.07)',
  padding: '22px 20px 34px',
  display: 'flex',
  gap: 20,
  justifyContent: 'center',
  flexWrap: 'wrap',
  fontSize: 13,
  color: '#64748B',
}

const footLink: CSSProperties = {color: '#94A3B8', textDecoration: 'none'}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 60,
  background: 'rgba(2,6,16,.92)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
}

const stage: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  maxWidth: '100%',
}

const stageMeta: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  fontSize: 14,
  color: '#94A3B8',
}

// Interactive bits (hover/focus/media queries) can't live in inline styles.
const css = `
/* Fixed column counts (not auto-fill) so the initial ${INITIAL_VISIBLE} cards always
   fill whole rows — every breakpoint divides 12 evenly. */
.tw-grid{display:grid;gap:16px;grid-template-columns:repeat(6,1fr)}
@media (max-width:1099px){.tw-grid{grid-template-columns:repeat(4,1fr)}}
@media (max-width:819px){.tw-grid{grid-template-columns:repeat(3,1fr)}}
@media (max-width:559px){.tw-grid{grid-template-columns:repeat(2,1fr);gap:12px}}

.tw-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;
  padding:12px 24px;border-radius:12px;border:0;cursor:pointer;font-weight:700;font-size:15px;
  color:#fff;text-decoration:none;background:linear-gradient(180deg,#0ea5e9,${ACCENT});
  box-shadow:0 6px 20px rgba(0,158,247,.34);transition:transform .16s ease,box-shadow .16s ease,filter .16s ease}
.tw-btn:hover{transform:translateY(-1px);box-shadow:0 10px 26px rgba(0,158,247,.44);color:#fff;filter:brightness(1.05)}
.tw-btn:active{transform:translateY(0)}
.tw-btn-sm{padding:8px 16px;font-size:14px;border-radius:10px}
.tw-btn-lg{padding:15px 32px;font-size:16.5px}
.tw-btn-ghost{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.14);
  box-shadow:none;color:#E2E8F0}
.tw-btn-ghost:hover{background:rgba(255,255,255,.11);box-shadow:none;color:#fff}

.tw-card{position:relative;display:block;width:100%;padding:0;aspect-ratio:9/16;overflow:hidden;
  border-radius:16px;border:1px solid rgba(255,255,255,.09);background:#111827;cursor:pointer;
  transition:transform .2s ease,border-color .2s ease,box-shadow .2s ease}
.tw-card:hover{transform:translateY(-4px);border-color:rgba(0,158,247,.5);
  box-shadow:0 16px 34px rgba(0,0,0,.5)}
.tw-card:focus-visible{outline:2px solid ${ACCENT};outline-offset:3px}
.tw-thumb{width:100%;height:100%;object-fit:cover;display:block}
.tw-shade{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.05) 40%,rgba(0,0,0,.55))}
.tw-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);
  width:52px;height:52px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  background:rgba(255,255,255,.92);color:#0B1120;padding-left:3px;
  box-shadow:0 6px 18px rgba(0,0,0,.4);transition:transform .2s ease,background .2s ease}
.tw-card:hover .tw-play{transform:translate(-50%,-50%) scale(1.1);background:#fff}
.tw-dur{position:absolute;bottom:10px;inset-inline-end:10px;padding:3px 8px;border-radius:7px;
  background:rgba(0,0,0,.62);color:#fff;font-size:12px;font-weight:600;letter-spacing:.02em;
  font-variant-numeric:tabular-nums;direction:ltr}

.tw-video{max-height:82vh;max-width:min(92vw,480px);border-radius:16px;background:#000;
  box-shadow:0 24px 60px rgba(0,0,0,.6);display:block}
.tw-x{position:absolute;top:18px;inset-inline-end:20px;width:42px;height:42px;border-radius:50%;
  border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.08);color:#fff;
  font-size:17px;cursor:pointer;transition:background .15s ease}
.tw-x:hover{background:rgba(255,255,255,.18)}
.tw-nav{position:absolute;top:50%;transform:translateY(-50%);width:48px;height:48px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.08);color:#fff;
  line-height:1;cursor:pointer;transition:background .15s ease}
.tw-nav:hover{background:rgba(255,255,255,.18)}
.tw-nav-next{left:20px}
.tw-nav-prev{right:20px}
@media (max-width:640px){
  .tw-nav{width:38px;height:38px}
  .tw-nav svg{width:20px;height:20px}
  .tw-nav-next{left:6px}
  .tw-nav-prev{right:6px}
}
@media (prefers-reduced-motion:reduce){
  .tw-btn,.tw-card,.tw-play{transition:none}
  .tw-btn:hover,.tw-card:hover{transform:none}
}
`

export default TestimonialsPage
